import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Select } from "@/components/ui/Select";
import { useActiveCompanies } from "@/hooks/useActiveCompanies";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import { bumpCompanyScopeVersion, setSelectedCompany, setSelectedStore } from "@/store/appSlice";
import { refreshSessionInit } from "@/store/sessionSlice";
import { invalidate } from "@/lib/query/invalidate";
import { notify } from "@/core/toast/notify";
import { clearPosSessionStorage } from "@/utils/posSessionStorage";

interface CompanySwitcherProps {
  compact?: boolean;
  className?: string;
}

export function CompanySwitcher({
  compact = true,
  className,
}: CompanySwitcherProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const qc = useQueryClient();
  const selected = useAppSelector((s) => s.app.selectedCompany);
  const sessionActiveCompanyId = useAppSelector((s) => s.session.activeCompanyId);
  const sessionLoaded = useAppSelector((s) => s.session.loaded);
  const { companies, loading } = useActiveCompanies();
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (selected || loading || companies.length === 0) return;
    const preferredId = sessionActiveCompanyId;
    const preferred = preferredId != null ? companies.find((c) => c.id === preferredId) : undefined;
    if (preferred) {
      dispatch(setSelectedCompany(preferred));
      return;
    }
    dispatch(setSelectedCompany(companies[0]));
  }, [companies, dispatch, loading, selected, sessionActiveCompanyId]);

  useEffect(() => {
    if (!selected || loading || companies.length === 0) return;
    if (!companies.some((c) => c.id === selected.id)) {
      dispatch(setSelectedCompany(null));
    }
  }, [companies, dispatch, loading, selected]);

  const onChange = async (value: string) => {
    const company = companies.find((c) => String(c.id) === value);
    if (!company || company.id === selected?.id || switching) return;
    setSwitching(true);
    try {
      await dispatch(refreshSessionInit(company.id)).unwrap();
      clearPosSessionStorage();
      dispatch(setSelectedStore(null));
      // Drop previous-company cache before remount so screens do not reuse stale rows.
      invalidate.companyScope(qc, company.id);
      dispatch(bumpCompanyScopeVersion());
      notify.t.success('workingIn', { name: company.name });
    } catch {
      // Keep the previous valid company active when validation or bootstrap fails.
      notify.t.error('companySwitchFailed');
    } finally {
      setSwitching(false);
    }
  };

  if (!loading && companies.length === 0) {
    return null;
  }

  if (!sessionLoaded && companies.length === 0) {
    return null;
  }

  return (
    <Select
      label={compact ? undefined : t('company.label')}
      className={className ?? "min-w-[9rem] max-w-[15rem]"}
      headerOverlay
      placeholder={loading ? t('company.loading') : t('company.select')}
      disabled={loading || switching || companies.length === 0}
      value={selected ? String(selected.id) : ""}
      options={companies.map((c) => ({
        value: String(c.id),
        label: `${c.name} (${c.code})`,
      }))}
      onChange={onChange}
      allowClear={false}
    />
  );
}
